/**
 * The Game State Check cascade and the bag window (CR §1.8, SPEC.md §4.1).
 *
 * The GSC is the engine's heartbeat. `rules.ts` owns *what* it checks — 20 lore, ending your
 * turn with an empty deck, damage at or above Willpower — and loops it to quiescence, because
 * "after a game state check completes, it immediately runs again" (CR §1.8.3). This module owns
 * what surrounds that loop:
 *
 * - **`settle`** — run the cascade to quiet and then put whatever it produced into the bag.
 *   Triggers arising from a GSC are added to the bag but do **not** resolve until the whole
 *   cascade has completed (CR §1.8.2): a banish-on-damage that fires new triggers re-runs the
 *   check before any bag ability resolves, and a win or loss found mid-cascade ends the game
 *   immediately, with the bag left untouched.
 * - **`bagWindowOpen`** — whether the frame on top of the resolution stack is a moment where
 *   bag abilities may resolve at all. Two moments say no: the Ready step, whose triggers wait
 *   for the Set step (CR §3.2.1–§3.2.2), and mid-effect, because a trigger that occurs while an
 *   effect is resolving waits for that effect to complete (CR §7.7.3.1).
 */
import { collectTriggers } from './bag.ts';
import type { GameStateCheckOptions } from './rules.ts';
import { runGameStateChecks } from './rules.ts';
import type { Frame, GameState } from './state.ts';

/**
 * Run the game state check cascade to quiescence, then collect the triggers it produced
 * (CR §1.8.2, §1.8.3). Called after every action and after every ability resolution — the
 * "GSC after all effects of an action or ability resolve" of CR §6.7.5.
 */
export function settle(state: GameState, options: GameStateCheckOptions = {}): void {
  runGameStateChecks(state, options);
  collectTriggers(state);
}

/**
 * May bag abilities resolve while this frame is on top of the resolution stack?
 *
 * Everything is a bag window except the two CR-mandated pauses, plus the frames that are
 * already the bag's own machinery.
 */
export function bagWindowOpen(frame: Frame): boolean {
  switch (frame.kind) {
    case 'turn-start':
      // `step` names what runs *next*, and the Beginning phase has exactly one bag window: the
      // end of the Set step, which is `draw`. Triggers raised by the Ready step wait for it
      // (CR §3.2.1), and so does "at the start of your turn" itself — the `phase-began` event
      // is emitted before the phase's first step, but the ability triggers during the Set step
      // and resolves at the end of it (CR §3.2.2). Resolving it any earlier would run it
      // before the Ready step readied the player's ink and reset the per-turn lists.
      return frame.step === 'draw';
    case 'bag':
    case 'resolve-ability':
    case 'effect':
      // Already resolving the bag, or mid-effect (CR §7.7.3.1) — a trigger raised while an
      // effect resolves waits for that effect to complete, including its player choices.
      return false;
    case 'enter-exerted':
      // Entering play is not finished until the Bodyguard choice is made (CR §8.2.1), so the
      // card's own "when you play" trigger must not resolve before it.
      return false;
    default:
      return true;
  }
}
